import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TopBar } from '../components/layout/TopBar';
import { useAppStore } from '../store/useStore';
import { reservationsAPI, tablesAPI, customersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { cn, formatTime, generateTimeSlots } from '../lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  Plus, 
  MoreVertical, 
  Clock, 
  Users, 
  Phone, 
  Mail,
  CalendarIcon,
  Check,
  X,
  UserX,
  Edit2,
  Trash2,
  Search,
  LayoutGrid,
  List,
  Download,
  FileText,
  FileSpreadsheet
} from 'lucide-react';
import ReservationCalendar from '../components/ReservationCalendar';

export default function Reservations() {
  const { t } = useTranslation();
  const { selectedDate, setSelectedDate, reservations, setReservations, tables, setTables } = useAppStore();
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customers, setCustomers] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [exporting, setExporting] = useState(false);

  const [formData, setFormData] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    date: selectedDate,
    time: '19:00',
    party_size: 2,
    duration: 90,
    table_ids: [],
    notes: '',
    special_requests: '',
    source: 'dashboard',
  });

  const timeSlots = generateTimeSlots('12:00', '23:00', 30);

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [resResponse, tablesResponse, customersResponse] = await Promise.all([
        reservationsAPI.list({ date_from: selectedDate, date_to: selectedDate }),
        tablesAPI.list(),
        customersAPI.list(),
      ]);
      setReservations(resResponse.data);
      setTables(tablesResponse.data);
      setCustomers(customersResponse.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingReservation(null);
    setFormData({
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      date: selectedDate,
      time: '19:00',
      party_size: 2,
      duration: 90,
      table_ids: [],
      notes: '',
      special_requests: '',
      source: 'dashboard',
    });
    setShowDialog(true);
  };

  const openEditDialog = (reservation) => {
    setEditingReservation(reservation);
    setFormData({
      guest_name: reservation.guest_name,
      guest_email: reservation.guest_email || '',
      guest_phone: reservation.guest_phone || '',
      date: reservation.date,
      time: reservation.time,
      party_size: reservation.party_size,
      duration: reservation.duration,
      table_ids: reservation.table_ids || [],
      notes: reservation.notes || '',
      special_requests: reservation.special_requests || '',
      source: reservation.source,
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingReservation) {
        const response = await reservationsAPI.update(editingReservation.id, formData);
        setReservations(reservations.map(r => r.id === editingReservation.id ? response.data : r));
        toast.success('Reservación actualizada');
      } else {
        const response = await reservationsAPI.create(formData);
        setReservations([...reservations, response.data]);
        toast.success('Reservación creada');
      }
      setShowDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleStatusChange = async (reservationId, newStatus) => {
    try {
      const response = await reservationsAPI.update(reservationId, { status: newStatus });
      setReservations(reservations.map(r => r.id === reservationId ? response.data : r));
      toast.success('Estado actualizado');
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleDelete = async (reservationId) => {
    try {
      await reservationsAPI.delete(reservationId);
      setReservations(reservations.filter(r => r.id !== reservationId));
      toast.success('Reservación eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Filter reservations
  const filteredReservations = reservations
    .filter(r => {
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          r.guest_name.toLowerCase().includes(search) ||
          r.guest_phone?.toLowerCase().includes(search) ||
          r.guest_email?.toLowerCase().includes(search)
        );
      }
      return true;
    })
    .filter(r => statusFilter === 'all' || r.status === statusFilter)
    .sort((a, b) => a.time.localeCompare(b.time));

  // Get available tables for selected time
  const getAvailableTables = () => {
    const occupiedTableIds = reservations
      .filter(r => 
        r.date === formData.date && 
        r.time === formData.time && 
        r.status !== 'cancelled' &&
        r.id !== editingReservation?.id
      )
      .flatMap(r => r.table_ids || []);
    
    return tables.filter(t => 
      !occupiedTableIds.includes(t.id) && 
      t.status !== 'blocked' &&
      t.capacity_max >= formData.party_size
    );
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await reservationsAPI.export(format, { 
        date_from: selectedDate, 
        date_to: selectedDate,
        status: statusFilter !== 'all' ? statusFilter : undefined
      });
      
      if (format === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `reservaciones_${selectedDate}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `reservaciones_${selectedDate}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      toast.success(`Exportado a ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="reservations-page">
      <TopBar 
        title={t('reservations.title')} 
        helpSection="reservations"
        actions={
          <div className="flex items-center gap-2">
            {/* Export Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" disabled={exporting} data-testid="export-reservations-btn">
                  <Download className={cn("h-4 w-4 mr-1", exporting && "animate-spin")} />
                  Exportar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('csv')}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Exportar CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('pdf')}>
                  <FileText className="h-4 w-4 mr-2" />
                  Exportar PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* View Toggle */}
            <div className="flex bg-muted rounded-lg p-1">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                data-testid="list-view-btn"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('calendar')}
                data-testid="calendar-view-btn"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
            <Button size="sm" onClick={openCreateDialog} data-testid="new-reservation-btn">
              <Plus className="h-4 w-4 mr-1" /> {t('reservations.new')}
            </Button>
          </div>
        }
      />

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="h-[calc(100vh-120px)]">
          <ReservationCalendar
            reservations={reservations}
            tables={tables}
            selectedDate={selectedDate}
            onDateChange={(date) => {
              setSelectedDate(format(date, 'yyyy-MM-dd'));
            }}
            onReservationClick={(reservation) => {
              openEditDialog(reservation);
            }}
          />
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
      <div className="p-6">
        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-reservations-input"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter-select">
              <SelectValue placeholder={t('common.filter')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              <SelectItem value="pending">{t('status.pending')}</SelectItem>
              <SelectItem value="confirmed">{t('status.confirmed')}</SelectItem>
              <SelectItem value="seated">{t('status.seated')}</SelectItem>
              <SelectItem value="completed">{t('status.completed')}</SelectItem>
              <SelectItem value="cancelled">{t('status.cancelled')}</SelectItem>
              <SelectItem value="no_show">{t('status.no_show')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Reservations List */}
        <div className="space-y-3">
          {filteredReservations.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">{t('reservations.noReservations')}</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={openCreateDialog}>
                  <Plus className="h-4 w-4 mr-1" /> Crear primera reservación
                </Button>
              </CardContent>
            </Card>
          ) : (
            filteredReservations.map((reservation) => {
              const assignedTables = tables.filter(t => reservation.table_ids?.includes(t.id));
              return (
                <Card 
                  key={reservation.id} 
                  className="hover-lift"
                  data-testid={`reservation-card-${reservation.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Time */}
                        <div className="flex flex-col items-center justify-center w-16 h-16 rounded-lg bg-primary/10 text-primary">
                          <Clock className="h-4 w-4" />
                          <span className="text-lg font-bold">{reservation.time}</span>
                        </div>

                        {/* Guest Info */}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{reservation.guest_name}</span>
                            <Badge variant="outline" className={`status-${reservation.status}`}>
                              {t(`status.${reservation.status}`)}
                            </Badge>
                            {reservation.source !== 'dashboard' && (
                              <Badge variant="secondary" className="text-xs">
                                {reservation.source}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" /> {reservation.party_size} {t('common.people')}
                            </span>
                            {reservation.guest_phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" /> {reservation.guest_phone}
                              </span>
                            )}
                            {reservation.guest_email && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" /> {reservation.guest_email}
                              </span>
                            )}
                          </div>
                          {assignedTables.length > 0 && (
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-xs text-muted-foreground">Mesa:</span>
                              {assignedTables.map(t => (
                                <Badge key={t.id} variant="outline" className="text-xs">
                                  {t.number}
                                </Badge>
                              ))}
                            </div>
                          )}
                          {reservation.notes && (
                            <p className="text-xs text-muted-foreground mt-1 italic">"{reservation.notes}"</p>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {/* Quick Status Actions */}
                        {reservation.status === 'pending' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleStatusChange(reservation.id, 'confirmed')}
                            data-testid={`confirm-btn-${reservation.id}`}
                          >
                            <Check className="h-4 w-4 mr-1" /> Confirmar
                          </Button>
                        )}
                        {reservation.status === 'confirmed' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleStatusChange(reservation.id, 'seated')}
                            data-testid={`seat-btn-${reservation.id}`}
                          >
                            <Users className="h-4 w-4 mr-1" /> Sentar
                          </Button>
                        )}
                        {reservation.status === 'seated' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleStatusChange(reservation.id, 'completed')}
                            data-testid={`complete-btn-${reservation.id}`}
                          >
                            <Check className="h-4 w-4 mr-1" /> Completar
                          </Button>
                        )}

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`more-actions-${reservation.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(reservation)}>
                              <Edit2 className="h-4 w-4 mr-2" /> Editar
                            </DropdownMenuItem>
                            {!['cancelled', 'no_show', 'completed'].includes(reservation.status) && (
                              <>
                                <DropdownMenuItem onClick={() => handleStatusChange(reservation.id, 'cancelled')}>
                                  <X className="h-4 w-4 mr-2" /> Cancelar
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange(reservation.id, 'no_show')}>
                                  <UserX className="h-4 w-4 mr-2" /> No-Show
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => handleDelete(reservation.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg" data-testid="reservation-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingReservation ? t('reservations.edit') : t('reservations.new')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            {/* Guest Info */}
            <div>
              <Label>{t('reservations.guestName')} *</Label>
              <Input
                value={formData.guest_name}
                onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                placeholder="Juan Pérez"
                data-testid="guest-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('reservations.phone')}</Label>
                <Input
                  value={formData.guest_phone}
                  onChange={(e) => setFormData({ ...formData, guest_phone: e.target.value })}
                  placeholder="+52 55 1234 5678"
                  data-testid="guest-phone-input"
                />
              </div>
              <div>
                <Label>{t('reservations.email')}</Label>
                <Input
                  type="email"
                  value={formData.guest_email}
                  onChange={(e) => setFormData({ ...formData, guest_email: e.target.value })}
                  placeholder="correo@ejemplo.com"
                  data-testid="guest-email-input"
                />
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('reservations.date')} *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="date-picker-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(new Date(formData.date), 'PPP', { locale: es })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={new Date(formData.date)}
                      onSelect={(date) => date && setFormData({ ...formData, date: format(date, 'yyyy-MM-dd') })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>{t('reservations.time')} *</Label>
                <Select 
                  value={formData.time} 
                  onValueChange={(value) => setFormData({ ...formData, time: value })}
                >
                  <SelectTrigger data-testid="time-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>{formatTime(time)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Party Size & Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('reservations.partySize')} *</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={formData.party_size}
                  onChange={(e) => setFormData({ ...formData, party_size: parseInt(e.target.value) })}
                  data-testid="party-size-input"
                />
              </div>
              <div>
                <Label>{t('reservations.duration')}</Label>
                <Select 
                  value={String(formData.duration)} 
                  onValueChange={(value) => setFormData({ ...formData, duration: parseInt(value) })}
                >
                  <SelectTrigger data-testid="duration-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="60">60 min</SelectItem>
                    <SelectItem value="90">90 min</SelectItem>
                    <SelectItem value="120">120 min</SelectItem>
                    <SelectItem value="150">150 min</SelectItem>
                    <SelectItem value="180">180 min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Table Assignment */}
            <div>
              <Label>{t('reservations.table')}</Label>
              <Select 
                value={formData.table_ids[0] || 'none'} 
                onValueChange={(value) => setFormData({ ...formData, table_ids: value && value !== 'none' ? [value] : [] })}
              >
                <SelectTrigger data-testid="table-select">
                  <SelectValue placeholder="Asignar mesa (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {getAvailableTables().map((table) => (
                    <SelectItem key={table.id} value={table.id}>
                      Mesa {table.number} ({table.capacity_max}p)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <Label>{t('reservations.notes')}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notas internas..."
                rows={2}
                data-testid="notes-textarea"
              />
            </div>

            <div>
              <Label>{t('reservations.specialRequests')}</Label>
              <Textarea
                value={formData.special_requests}
                onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                placeholder="Mesa junto a la ventana, cumpleaños..."
                rows={2}
                data-testid="special-requests-textarea"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={!formData.guest_name || !formData.date || !formData.time}
              data-testid="submit-reservation-btn"
            >
              {editingReservation ? t('common.save') : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
